import type React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';

// Custom hook to track dimension of a component
// Taken and modified from https://stackoverflow.com/a/60218754
export const useOnElementResize = <T extends HTMLElement>(
  targetRef: React.RefObject<T>,
): Record<'width' | 'height', number> => {
  const [width, setWidth] = useState(targetRef.current?.offsetWidth ?? 0);
  const [height, setHeight] = useState(targetRef.current?.offsetHeight ?? 0);
  const observer = useRef<ResizeObserver>();

  useEffect(() => {
    function outputsize() {
      setWidth(targetRef.current?.offsetWidth ?? 0);
      setHeight(targetRef.current?.offsetHeight ?? 0);
    }

    observer.current = new ResizeObserver(outputsize);
    const el = targetRef.current as Element | null;
    if (el) observer.current.observe(el);

    return () => {
      if (el) observer.current?.disconnect();
    };
  }, [targetRef]);

  return useMemo(
    () => ({
      width,
      height,
    }),
    [width, height],
  );
};
